<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { onMount } from 'svelte';
	import { format } from '../../../../services/i18n';
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import {
		capFirstChar,
		formatSnakeCaseToTitle,
		transformedTimeZones,
	} from '../../../../helpers/utilities';
	import { getNumberInputConfig } from '../../../../helpers/number_input_helpers';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import { getTimeZoneName, validateGeolocation } from '../../../../helpers/geolocalion_helper';
	import { localReq, postReq } from '../../../../services/http';
	import { devices_settings } from 'src/stores/device-runtime/status';
	import SelectDeviceLocation from '../../../Maps/SelectDeviceLocation.svelte';
	import DeviceLocationPreview from '../../../Maps/DeviceLocationPreview.svelte';
	import {
		Alert,
		ButtonV2,
		InputV2,
		Loader,
		Radio,
		SelectV2,
		Text,
		ToggleV2,
		toast,
	} from '@Shelly/shelly-design-system';
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Device } from '../../../../types/Device';
	import { removeOrAddUnderscore } from 'src/helpers/utilities';
	import { coordinatesValidationHelper } from 'src/helpers/coordinates_helpers';
	import SettingGuide from '../../SettingGuide.svelte';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;

	const id = real_id(device.id),
		settings = devices_settings.getByRealId(id),
		loading = { detect: false, save: false };

	let timeZones = null;
	let timeZone = settings.timezone;
	let cityOffset = null;
	let latitude = settings.lat,
		longitude = settings.lng,
		tzautodetect = settings.tzautodetect,
		daylight = settings.tz_dst_auto ? 'auto' : settings.tz_dst ? 'on' : 'off',
		showModal = false;

	onMount(async () => {
		if (!validateGeolocation(latitude, longitude)) {
			latitude = 0;
			longitude = 0;
		}
		await getTimeZones();
	});

	const getTimeZones = async () => {
		const res: SettingRequest = await postReq(`/timezone/tzoffsetlist`, {});

		if (res.isok) {
			timeZones = transformedTimeZones(Object.keys(res.data));

			timeZone = timeZones?.find((el) => el.stringToSend === timeZone)?.stringToVisualise;

			cityOffset = res.data[removeOrAddUnderscore(timeZone, 'addUnderscore')];
		} else {
			timeZones = [];
		}
	};

	const saveLocationSettings = async () => {
		if (
			latitude >= 90 &&
			latitude.toString().includes('.') &&
			latitude.toString().split('.')[1].startsWith('00000')
		) {
			latitude = +latitude.toString().split('.')[0];
		}

		if (
			longitude >= 180 &&
			longitude.toString().includes('.') &&
			longitude.toString().split('.')[1].startsWith('00000')
		) {
			longitude = +longitude.toString().split('.')[0];
		}

		if (!validateGeolocation(latitude, longitude)) {
			toast.open({
				type: 'danger',
				title: $format('invalid_arguments', { default: 'Invalid arguments' }),
				text: $format('text_invalid_arguments', {
					default:
						"'Latitude' should be greater than -90 and less than 90, 'longitude' should be greater than -180 and less than 180!",
				}),
			});
			return;
		}

		loading.save = true;

		const timezone = timeZones.find((el) => el.stringToVisualise === timeZone).stringToSend;

		const params = {
				tzautodetect,
				latitude,
				longitude,
				timezone,
				tz_utc_offset: cityOffset,
				tz_dst: Number(daylight == 'on'),
				tz_dst_auto: Number(daylight == 'auto'),
			},
			localIP = get_local_ip(id);
		let res: SettingRequest;
		if (localIP) {
			res = await localReq(device, `${localIP}/settings`, 'post', {
				...params,
				id,
				lat: latitude,
				lng: longitude,
			});
		} else {
			res = await postReq(`/device/settings/timezone`, {
				...params,
				id,
				auto_location: Number(tzautodetect),
			});
		}
		if (res.isok) {
			devices_settings.update_devices_settings(device.id, {
				...settings,
				...params,
			});
		}
		onSettingsResponse(res, device, {
			success: $format('location_save_scc_toast', {
				default: 'Location settings saved successfully.',
			}),
			danger: $format('location_save_err_toast', {
				default: 'Failed to save location settings!',
			}),
		});
		loading.save = false;
	};

	const detectLocationSettings = async () => {
		loading.detect = true;
		const res: SettingRequest = await postReq(`/timezone/autodetect`, '');
		if (res.isok) {
			timeZone = removeOrAddUnderscore(res.data.time_zone, 'removeUnderscore');
			latitude = res.data.latitude;
			longitude = res.data.longitude;
		} else {
			toast.open({
				type: 'danger',
				title: $format('location_detect_err_toast', {
					default: 'Failed to auto detect time zone!',
				}),
			});
		}
		loading.detect = false;
	};

	const onSelectMapLocation = async (e: CustomEvent) => {
		latitude = Number(e.detail.lat.toFixed(6));
		longitude = Number(e.detail.lng.toFixed(6));
		const newTimeZone = await getTimeZoneName(e.detail.lat, e.detail.lng);
		if (![undefined, 'uninhabited', ''].includes(newTimeZone)) {
			timeZone = newTimeZone;
			await getTimeZones();
		}
	};

	const openMapModal = () => {
		if (
			latitude >= 90 &&
			latitude.toString().includes('.') &&
			latitude.toString().split('.')[1].startsWith('00000')
		) {
			latitude = +latitude.toString().split('.')[0];
		}

		if (
			longitude >= 180 &&
			longitude.toString().includes('.') &&
			longitude.toString().split('.')[1].startsWith('00000')
		) {
			longitude = +longitude.toString().split('.')[0];
		}

		if (!validateGeolocation(latitude, longitude)) {
			latitude = settings.lat;
			longitude = settings.lng;
		}
		showModal = true;
	};

	$: {
		if (latitude) latitude = coordinatesValidationHelper(latitude.toString());
		if (longitude) longitude = coordinatesValidationHelper(longitude.toString());
	}

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if (timeZones?.length && !tzautodetect) {
			setStickyBottomAction({
				onClick: saveLocationSettings,
				loading: loading.save,
				disabled: loading.detect || loading.save,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

{#if timeZones == null}
	<div class="geo-loading">
		<Loader size="l" />
		<Text styleType="body-small" class="text-color-muted mt-s text-center">
			{$format('settings_loading_location_and_time_zone', {
				default: 'Fetching location and time zone from the device…',
			})}
		</Text>
	</div>
{:else if timeZones.length === 0}
	<Alert
		type="danger"
		class="mb-s"
		heading={$format('device_timezones_error', {
			default: 'Cloud not get device timezones.',
		})}
	/>
{:else}
	<div class="geo-settings">
		<SettingGuide
			text={$format('settings_guide_location_and_time', {
				default:
					'Used for schedules, sunrise and sunset, and energy reports. Set this device’s location and time zone, or let it detect them automatically.',
			})}
		/>
		<div>
			<ToggleV2
				size="m"
				bind:checked={tzautodetect}
				id="toggle-geo-auto-detect"
				on:click={saveLocationSettings}
			>
				{$format('automatically_detect_geo_location', {
					default: 'Automatically detect time-zone and Geo location.',
				})}
			</ToggleV2>

			{#if tzautodetect}
				<Text styleType="label-small" class="ml-xl pl-m text-uppercase text-color-muted">
					{$format('location_time_zone', { default: 'time zone' })}: {timeZone || '—'}
				</Text>
			{/if}
		</div>

		<DeviceLocationPreview
			lat={Number(latitude)}
			lng={Number(longitude)}
			disabled={tzautodetect || loading.save || loading.detect}
			on:open={openMapModal}
		/>

		{#if showModal}
			<SelectDeviceLocation
				bind:showModal
				lat={latitude}
				lng={longitude}
				on:change={onSelectMapLocation}
			/>
		{/if}

		{#if !tzautodetect}
			<ButtonV2
				size="m"
				loading={loading.detect}
				width="full"
				disabled={loading.save}
				variant="secondary"
				on:click={detectLocationSettings}
			>
				{capFirstChar(
					$format('button_location_auto_detect', { default: 'auto detect location' }),
				)}
			</ButtonV2>

			<div class="geo-settings-group">
				<SelectV2
					size="m"
					width="full"
					label={formatSnakeCaseToTitle(
						$format('location_time_zone', { default: 'time zone' }),
					)}
					bind:value={timeZone}
					isAutocomplete
					icon="fas fa-globe"
					options={timeZones.map((t) => t.stringToVisualise)}
					id="select-xx3bnc9"
					aria-label={$format('location_time_zone', { default: 'time zone' })}
				/>
			</div>

			<div class="geo-settings-group">
				<Text styleType="body-small">
					{$format('daylight_save_time', { default: 'Daylight saving time' })}
				</Text>
				<Radio size="m" bind:selected={daylight} value="auto" id="radio-9d3hm5p"
					>{$format('option_action_auto', { default: 'Auto' })}</Radio
				>
				<Radio size="m" bind:selected={daylight} value="on" id="radio-z4u0w0r"
					>{$format('option_action_on', { default: 'On' })}</Radio
				>
				<Radio size="m" bind:selected={daylight} value="off" id="radio-vidt70a"
					>{$format('option_action_off', { default: 'Off' })}</Radio
				>
			</div>

			<div class="geo-settings-group">
				<InputV2
					{...getNumberInputConfig()}
					size="m"
					label={formatSnakeCaseToTitle(
						$format('location_latitude', { default: 'latitude' }),
					)}
					bind:value={latitude}
					min={-90}
					max={90}
					step={0.000001}
					id="input-tyfg9i3"
					aria-label={$format('location_latitude', { default: 'latitude' })}
				/>
			</div>

			<div class="geo-settings-group">
				<InputV2
					{...getNumberInputConfig()}
					size="m"
					label={formatSnakeCaseToTitle(
						$format('location_longtitude', { default: 'longitude' }),
					)}
					bind:value={longitude}
					min={-180}
					max={180}
					step={0.000001}
					id="input-045cs65"
					aria-label={$format('location_longtitude', { default: 'longitude' })}
				/>
			</div>
		{/if}
	</div>
{/if}

<style>
	.geo-loading {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-height: 240px;
		width: 100%;
	}

	.geo-settings {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		width: 100%;
	}

	.geo-settings > :global(*) {
		margin-bottom: 12px;
		width: 100%;
	}

	.geo-settings-group {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
		width: 100%;
	}
</style>
